/**
 * MosqueListSheet — mobile bottom sheet showing all nearby mosques
 * sorted by: biryani confirmed > unverified report > no report
 * Shows distance from user location if available.
 */
import { useMemo } from 'react';

function getDistanceM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(m) {
  if (m < 1000) return `${Math.round(m)} মি`;
  return `${(m / 1000).toFixed(1)} কি.মি`;
}

function statusWeight(mosque) {
  const t = mosque.today;
  if (!t?.report_id) return 3; // no report
  if (t.score > 0) return 1;   // confirmed biryani
  if (t.score < 0) return 2;   // likely false
  return 2;                     // unverified
}

export default function MosqueListSheet({
  mosques,
  userLocation,
  onSelectMosque,
  onReportMosque,
  isDark = false,
  onClose,
}) {
  const bg = isDark ? 'rgba(18,18,18,0.98)' : 'rgba(255,255,255,0.98)';
  const textColor = isDark ? '#f1f5f9' : '#1f2937';
  const subColor = isDark ? '#94a3b8' : '#64748b';
  const borderColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const cardHover = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';

  const sorted = useMemo(() => {
    return [...mosques].sort((a, b) => {
      const sw = statusWeight(a) - statusWeight(b);
      if (sw !== 0) return sw;
      // same status → sort by distance
      if (userLocation) {
        const da = getDistanceM(userLocation.lat, userLocation.lng, a.lat, a.lng);
        const db = getDistanceM(userLocation.lat, userLocation.lng, b.lat, b.lng);
        return da - db;
      }
      return 0;
    });
  }, [mosques, userLocation]);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        zIndex: 500,
        background: bg,
        borderRadius: '24px 24px 0 0',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* drag handle + header */}
      <div style={{ padding: '12px 20px 0', borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', margin: '0 auto 12px' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h2 style={{ color: textColor, fontSize: 16, fontWeight: 700 }}>আশেপাশের মসজিদ</h2>
            <p style={{ color: subColor, fontSize: 12, marginTop: 2 }}>
              {sorted.length} টি মসজিদ পাওয়া গেছে
              {userLocation ? ' • দূরত্ব অনুযায়ী সাজানো' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: subColor, fontSize: 22, lineHeight: 1, padding: 4 }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* list */}
      <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0 24px' }}>
        {sorted.map((mosque) => {
          const t = mosque.today;
          const hasReport = t?.report_id;
          const confirmed = hasReport && t.score > 0;
          const denied = hasReport && t.score < 0;
          const dist = userLocation
            ? getDistanceM(userLocation.lat, userLocation.lng, mosque.lat, mosque.lng)
            : null;

          return (
            <div
              key={mosque.id}
              onClick={() => onSelectMosque(mosque)}
              style={{
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                cursor: 'pointer',
                borderBottom: `1px solid ${borderColor}`,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = cardHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {/* status dot */}
              <div style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                background: confirmed
                  ? 'rgba(16,185,129,0.12)'
                  : denied
                    ? 'rgba(239,68,68,0.1)'
                    : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22,
                border: confirmed
                  ? '1.5px solid rgba(16,185,129,0.4)'
                  : denied
                    ? '1.5px solid rgba(239,68,68,0.3)'
                    : `1.5px solid ${borderColor}`,
              }}>
                🕌
              </div>

              {/* info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: textColor, fontWeight: 600, fontSize: 14, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {mosque.name}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {hasReport ? (
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                      background: confirmed ? '#10b981' : denied ? '#ef4444' : '#f59e0b',
                      color: 'white',
                    }}>
                      {confirmed ? '✓ বিরিয়ানি আছে' : denied ? '✗ নেই' : '? যাচাই চলছে'}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: subColor }}>রিপোর্ট নেই</span>
                  )}
                  {dist !== null && (
                    <span style={{ fontSize: 11, color: subColor }}>• {formatDistance(dist)}</span>
                  )}
                </div>
              </div>

              {/* vote counts + report button */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                {hasReport && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ fontSize: 12, color: '#10b981', fontWeight: 700 }}>✓{t.yes_count}</span>
                    <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 700 }}>✗{t.no_count}</span>
                  </div>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); onReportMosque(mosque); }}
                  style={{
                    fontSize: 11, fontWeight: 700,
                    padding: '4px 10px', borderRadius: 99,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white', border: 'none', cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(16,185,129,0.35)',
                  }}
                >
                  + রিপোর্ট
                </button>
              </div>
            </div>
          );
        })}

        {sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: subColor }}>
            <p style={{ fontSize: 32, marginBottom: 12 }}>🕌</p>
            <p style={{ fontSize: 14, fontWeight: 600 }}>আশেপাশে কোনো মসজিদ পাওয়া যায়নি</p>
            <p style={{ fontSize: 12, marginTop: 6 }}>Map-এ double-click করে মসজিদ যোগ করুন</p>
          </div>
        )}
      </div>
    </div>
  );
}
